import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { PageShell } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import useSeo from "../hooks/useSeo";
import useContentHreflang from "../hooks/useContentHreflang";
import useContentRedirect from "../hooks/useContentRedirect";
import ContentLangSwitcher from "../components/ContentLangSwitcher";
import { API, pickContent, splitParagraphs, fileUrl } from "../lib/content";

const SITE = "https://lifeisaproject.global";

/**
 * MemoireDetailPage — /memoire/:slug. A single lived experience:
 * main content first, then the optional reflective sections (Context →
 * Experience → Reflection → Lesson → Growth → Note to reader) rendered
 * only when they contain text, then tags and related entries.
 *
 * CmsMemoireView is the pure renderer — also used by the token-protected
 * draft preview (/preview/:token), so a draft looks exactly like the
 * final published page.
 */

const MEMOIRE_SECTIONS = [
    ["context",        { en: "Context",         es: "Contexto",          nl: "Context",              fr: "Contexte" }],
    ["experience",     { en: "The experience",  es: "La experiencia",    nl: "De ervaring",          fr: "L’expérience" }],
    ["reflection",     { en: "Reflection",      es: "Reflexión",         nl: "Reflectie",            fr: "Réflexion" }],
    ["lesson",         { en: "The lesson",      es: "La lección",        nl: "De les",               fr: "La leçon" }],
    ["growth",         { en: "Growth",          es: "Crecimiento",       nl: "Groei",                fr: "Croissance" }],
    ["note_to_reader", { en: "Note to the reader", es: "Nota al lector", nl: "Notitie voor de lezer", fr: "Note au lecteur" }],
];

const RELATED_LABEL = {
    en: "Connected", es: "Relacionado", nl: "Verbonden", fr: "Lié",
};
const TYPE_LABEL = {
    ideas:    { en: "Idea", es: "Idea", nl: "Ide", fr: "Idée" },
    research: { en: "Research", es: "Investigación", nl: "Onderzoek", fr: "Recherche" },
    projects: { en: "Project", es: "Proyecto", nl: "Project", fr: "Projet" },
    memoire:  { en: "Memoire", es: "Memoire", nl: "Memoire", fr: "Mémoire" },
};

export function CmsMemoireView({ item, previewToken = null }) {
    const { lang } = useLanguage();

    const { title, subtitle: dek, body } = pickContent(item, lang);
    const paragraphs = splitParagraphs(body);
    const sections = item ? MEMOIRE_SECTIONS.filter(([key]) => (item[key] || "").trim()) : [];
    const related = item?.related_items || [];

    useSeo(item ? {
        title: item.seo_title || `${title} — Memoire · Life is a Project`,
        description: item.seo_description || dek || undefined,
        path: `/memoire/${item.slug}`,
        image: item.cover_image ? fileUrl(item.cover_image) : undefined,
    } : null);

    useContentHreflang(item, "memoire");

    useJsonLd(item ? `article-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${SITE}/memoire/${item.slug}`,
        headline: item.title,
        description: item.seo_description || item.subtitle || undefined,
        articleSection: "Memoire",
        inLanguage: item.language || "en",
        mainEntityOfPage: `${SITE}/memoire/${item.slug}`,
        url: `${SITE}/memoire/${item.slug}`,
        isPartOf: { "@id": `${SITE}/#website` },
        publisher: { "@id": `${SITE}/#organization` },
        author: { "@type": "Organization", name: "Life is a Project", url: SITE },
    } : null);

    useJsonLd(item ? `breadcrumb-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Memoire", item: `${SITE}/memoire` },
            { "@type": "ListItem", position: 3, name: item.title, item: `${SITE}/memoire/${item.slug}` },
        ],
    } : null);

    return (
        <PageShell testId="memoire-reader" backdrop={<PageBackdrop variant="memoire" soft />}>
            {!previewToken && (
                <Link to="/memoire" data-testid="memoire-back" className="link-editorial font-mono text-[11px] tracking-[0.28em] uppercase inline-block mb-10">
                    {lang === "es" ? "← Memoire" : lang === "nl" ? "← Memoire" : lang === "fr" ? "← Mémoire" : "← Memoire"}
                </Link>
            )}

            <header className="max-w-[820px] mb-14">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span aria-hidden className="gold-thin" />
                    <span>Memoire</span>
                    <span aria-hidden className="text-[color:var(--line-strong)]">·</span>
                    <span>{(item.language || "en").toUpperCase()}</span>
                </div>
                <ContentLangSwitcher item={item} basePath="memoire" />
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light tracking-[-0.01em] leading-[1.05] text-[color:var(--navy)]"
                    style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
                >
                    {title}
                </motion.h1>
                {dek && (
                    <p className="mt-8 font-display italic text-[22px] md:text-[26px] leading-[1.3] text-[color:var(--navy)]/80 max-w-[52ch]">
                        {dek}
                    </p>
                )}
                {(item.author || item.publication_date) && (
                    <p data-testid="memoire-meta" className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {[item.author, item.publication_date].filter(Boolean).join(" · ")}
                    </p>
                )}
            </header>

            {item.cover_image && (
                <img
                    src={fileUrl(item.cover_image, previewToken)}
                    alt={title}
                    data-testid="content-cover-image"
                    className="w-full h-auto mb-14 border border-[color:var(--line)]"
                    loading="lazy"
                />
            )}

            {paragraphs.length > 0 && (
                <article className="max-w-[68ch] space-y-6">
                    {paragraphs.map((p, i) => (
                        <p key={i} className={`text-[17px] md:text-[19px] leading-[1.75] text-[color:var(--navy)]/90 ${i === 0 ? "first-letter:font-display first-letter:text-[64px] first-letter:leading-[0.9] first-letter:mr-2 first-letter:float-left first-letter:mt-1" : ""}`}>
                            {p}
                        </p>
                    ))}
                </article>
            )}

            {sections.length > 0 && (
                <div className="mt-16 border-t border-[color:var(--line-strong)]">
                    {sections.map(([key, labels], idx) => (
                        <section key={key} data-testid={`memoire-section-${key}`} className="grid grid-cols-12 gap-6 py-10 md:py-12 border-b border-[color:var(--line)]">
                            <div className="col-span-12 md:col-span-3">
                                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                <h2 className="mt-3 font-display text-[24px] md:text-[28px] leading-[1.1] text-[color:var(--navy)]">
                                    {labels[lang] || labels.en}
                                </h2>
                            </div>
                            <div className="col-span-12 md:col-span-9 space-y-5 max-w-[62ch]">
                                {splitParagraphs(item[key]).map((p, i) => (
                                    <p key={i} className="text-[16px] md:text-[18px] leading-[1.72] text-[color:var(--navy)]/90">{p}</p>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {(item.tags || []).length > 0 && (
                <div className="mt-12 flex flex-wrap gap-2" data-testid="memoire-tags">
                    {item.tags.map((t) => (
                        <span key={t} className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--warm-gray-2)] border border-[color:var(--line)] px-2 py-0.5">
                            {t}
                        </span>
                    ))}
                </div>
            )}

            {related.length > 0 && (
                <div className="mt-14 pt-8 border-t border-[color:var(--line)]" data-testid="memoire-related">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-5">
                        {RELATED_LABEL[lang] || RELATED_LABEL.en}
                    </div>
                    <ul className="space-y-4">
                        {related.map((r) => (
                            <li key={`${r.type}-${r.id}`}>
                                <Link
                                    to={`/${r.type}/${r.slug}`}
                                    data-testid="memoire-related-link"
                                    className="group inline-flex items-baseline gap-4"
                                >
                                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--warm-gray-2)]">
                                        {(TYPE_LABEL[r.type] || TYPE_LABEL.memoire)[lang] || "Memoire"}
                                    </span>
                                    <span className="font-display italic text-[18px] text-[color:var(--navy)]/85 group-hover:text-[color:var(--navy)] transition-colors">
                                        {r.title}
                                    </span>
                                    <span aria-hidden className="w-5 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-9" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </PageShell>
    );
}

export default function MemoireDetailPage() {
    const { slug } = useParams();
    const { lang } = useLanguage();
    const [item, setItem] = useState(null);
    const [status, setStatus] = useState("loading");
    const navigate = useNavigate();
    useContentRedirect(status === "missing");

    useEffect(() => {
        setStatus("loading");
        setItem(null);
        axios
            .get(`${API}/content/memoire/${slug}`)
            .then((r) => { setItem(r.data); setStatus("ready"); })
            .catch(() => setStatus("missing"));
    }, [slug]);

    if (status === "loading") {
        return (
            <PageShell testId="memoire-reader">
                <div className="py-16 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                    {lang === "es" ? "Cargando…" : lang === "nl" ? "Laden…" : lang === "fr" ? "Chargement…" : "Loading…"}
                </div>
            </PageShell>
        );
    }

    if (status === "missing" || !item) {
        return (
            <PageShell testId="memoire-reader">
                <div className="py-16">
                    <p className="font-display italic text-[24px] md:text-[32px] leading-[1.2] text-[color:var(--navy)] max-w-[36ch]">
                        {lang === "es" ? "Este texto no está disponible." : lang === "nl" ? "Dit stuk is niet beschikbaar." : lang === "fr" ? "Ce texte n’est pas disponible." : "This piece is not available."}
                    </p>
                    <button
                        onClick={() => navigate("/memoire")}
                        className="mt-10 link-editorial font-mono text-[11px] tracking-[0.28em] uppercase"
                    >
                        ← Memoire
                    </button>
                </div>
            </PageShell>
        );
    }

    return <CmsMemoireView item={item} />;
}
